import '@geoman-io/leaflet-geoman-free'
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css'
import { createApp, toRaw } from 'vue'
import { merge } from 'lodash-unified'
import L, { Icon, Point } from 'leaflet'
import StyleEditor from '@map-sdk/components/drawStyleEditor'
import type { GeoJsonObject } from 'geojson'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

import 'leaflet-semicircle'
import 'leaflet-ellipse'

export interface StylePolygon {
  color?: string
  weight?: number
  opacity?: number
  fillColor?: string
  fillOpacity?: number
  dashArray?: number[]
  fill?: boolean
  stroke?: boolean
  editComonent?: any
  data?: GeoJsonObject | GeoJsonObject[]
  [key: string]: any
}

// Marker: 代表地图上的一个点状标记。通常用于表示特定地理位置上的兴趣点（POI）、导航指示点、或者任何需要在地图上精确定位的点状数据。Marker 可以包含图标（如自定义图片）和文字标签（tooltip 或 popup），并可以进行交互，如点击弹出详细信息。

// Circle: 表示地图上的圆形区域。由中心点坐标和半径定义，可用于表示圆形范围、覆盖区、影响区域等。Circle 也可以设置填充色、边框样式、透明度等属性，并支持交互功能。

// Line: 表示地图上的线段或折线。用于绘制路径、边界、流线、航线等连续的线性特征。Line 由一系列坐标点组成，可以设置线型（实线、虚线等）、颜色、宽度等样式属性，并支持鼠标悬停、点击等交互。

// Rectangle: 代表地图上的矩形区域。由对角线上的两个点确定其位置和尺寸。Rectangle 可以用来表示矩形建筑物、规划区域、监测范围等，支持填充、描边等样式设置以及相关的交互事件。

// Polygon: 表示地图上的多边形区域。由一系列闭合的坐标点序列构成，可以表示复杂的不规则区域，如地块、湖泊、行政区划等。Polygon 支持填充色、边框样式、洞穴（holes）等特性，并可进行交互操作。

// Cut: 如果出现在这个列表中，可能是指一种编辑操作，即通过绘制另一图形来“切割”已有图形的功能。在 GIS 和地图编辑工具中，这通常涉及到拓扑操作，允许用户分割或修剪已有的 Polygon、Line 等图层。@geoman-io/leaflet-geoman-free 中的 pm:cut 事件就是与此相关的。

// CircleMarker: 类似于 Marker，但表现为固定半径的圆形标记，而非自定义图标。常用于表示相对较小的点状数据，如气象站、传感器位置等，当不需要复杂图标或精确指向箭头时使用。

// ImageOverlay: 将一张图像以指定地理坐标范围的方式叠加在地图上。常用于显示历史地图、卫星影像、热力图、航拍照片等。ImageOverlay 可以调整透明度、缩放行为，并支持平移和缩放时保持相对于地图的固定位置。

// Text: 表示在地图上直接显示的文本标签。虽然 Leaflet 核心库本身并未内置 Text 图层，但可以通过插件（如 Leaflet.TextPath、Leaflet.label 等）实现这一功能，用于标注地点名称、说明文字、数值等信息。文本可以设置字体、颜色、大小、对齐方式等样式，并可随地图视图动态更新位置。
export type DrawType =
  | 'Marker'
  | 'Circle'
  | 'Line'
  | 'Rectangle'
  | 'Polygon'
  | 'Cut'
  | 'CircleMarker'
  | 'ImageOverlay'
  | 'Text'
let geojsonLayers: L.FeatureGroup<any>
export class DrawManager {
  //多边形默认样式
  geoJsonStylePolygon: StylePolygon = {
    color: '#ff3333',
    weight: 3,
    opacity: 1,
    fillColor: '#ff3333',
    fillOpacity: 0.2,
    dashArray: [5, 5],
    fill: true,
    stroke: true,
  }
  _map: MyMap
  _options: StylePolygon
  gsonLayer: Record<string, any> = {}
  constructor(map: MyMap, options?: StylePolygon) {
    this._map = map
    this._options = merge(this.geoJsonStylePolygon, options)
    this.init()
  }
  init() {
    geojsonLayers = L.featureGroup([]).addTo(this._map)
    //监听图层鼠标事件
    geojsonLayers.on('click', this.onClickGeojsonLayers)

    const customTranslation = {
      tooltips: {
        finishLine: '单击任何存在的标记或者双击以完成',
        finishPoly: '单击第一个标记或者双击完成以完成',
      },
    }
    this._map.pm.setLang('zh', customTranslation, 'customName')
    this._map.pm.addControls({
      position: 'topleft',
      drawMarker: false,
      drawCircleMarker: false,
      drawCircle: false,
      drawPolyline: false,
      drawRectangle: false,
      drawPolygon: false,
      editMode: false,
      dragMode: false,
      cutPolygon: false,
      drawText: false,
      removalMode: false,
      drawEllipse: false,
      rotateMode: false,
      ...this._options.pmConfig,
    })

    // // 自定义扇形工具示例
    // this._map.pm.Toolbar.createCustomControl({
    //   name: '扇形',
    //   block: 'draw',
    //   // icon: 'fa fa-pie-chart', // 使用Font Awesome图标或其他图标库
    //   onClick: () => {
    //     // 触发你的扇形绘制逻辑
    //     const centerLatLng = this._map.getCenter() // 示例：以地图中心为默认位置
    //     L.semiCircle(centerLatLng, {
    //       // 中心点坐标
    //       radius: 60, // 半径，单位米
    //       startAngle: -45, // 起始角度，以度为单位，从北方开始逆时针计算
    //       stopAngle: 45, // 结束角度
    //       fillColor: '#ff7800', // 填充颜色
    //       fillOpacity: 0.5, // 填充透明度
    //       stroke: true,
    //       color: '#000', // 边缘颜色
    //       dashArray: [],
    //       weight: 3,
    //       opacity: 1,
    //       clickable: true,
    //       draggable: false, // 是否可拖动，默认为false
    //       keyboard: true,
    //       zIndexOffset: 1000
    //     }).addTo(this._map) // 将扇形添加到地图实例上
    //   }
    // })
    // // 自定义同心圆工具示例
    // this._map.pm.Toolbar.createCustomControl({
    //   name: '同心圆',
    //   block: 'draw',
    //   onClick: () => {
    //     // 假设你已经有了基础圆的LatLng坐标和半径
    //     const center = this._map.getCenter() // 示例中心点
    //     const baseRadius = 60 // 基础圆的半径
    //     let newRadius = baseRadius * 2 // 新圆的半径，例如基础圆的两倍

    //     //绘制同心圆
    //     //设置圆心坐标
    //     for (let i = 0; i < 2; i++) {
    //       //添加圆
    //       L.circle(center, {
    //         //圆半径
    //         radius: newRadius,
    //         //颜色
    //         color: '#ffcc33',
    //         //填充色
    //         // fillColor: 'white',
    //         //填充色透明度
    //         fillOpacity: 0.5
    //       }).addTo(this._map)
    //       //调整半径大小
    //       newRadius = newRadius / 1.5
    //     }
    //   }
    // })
    // // 自定义网格工具示例
    // this._map.pm.Toolbar.createCustomControl({
    //   name: '网格',
    //   block: 'draw',
    //   onClick: () => {
    //     //绘制矩形网格
    //     //绘制经线
    //     for (let i = 0; i < 11; i++) {
    //       L.polyline(
    //         [
    //           [36.18, 120.36 + i * 0.001],
    //           [36.19, 120.36 + i * 0.001]
    //         ],
    //         { color: 'red' }
    //       ).addTo(this._map)
    //     }
    //     //绘制纬线
    //     for (let j = 0; j < 11; j++) {
    //       L.polyline(
    //         [
    //           [36.18 + j * 0.001, 120.36],
    //           [36.18 + j * 0.001, 120.37]
    //         ],
    //         { color: 'red' }
    //       ).addTo(this._map)
    //     }

    //     //绘制平行四边形网格
    //     //绘制经线，倾斜
    //     for (let i = 0; i < 9; i++) {
    //       L.polyline(
    //         [
    //           [36.13, 120.33 + i * 0.00375],
    //           [36.16, 120.33 + (i + 1) * 0.00375]
    //         ],
    //         { color: 'red' }
    //       ).addTo(this._map)
    //     }

    //     //绘制纬线,水平
    //     for (let j = 0; j < 9; j++) {
    //       L.polyline(
    //         [
    //           [36.13 + j * 0.00375, 120.33 + (j * 0.00375) / 8],
    //           [36.13 + j * 0.00375, 120.36 + (j * 0.00375) / 8]
    //         ],
    //         { color: 'red' }
    //       ).addTo(this._map)
    //     }
    //   }
    // })

    // // 自定义椭圆工具示例
    // this._map.pm.Toolbar.createCustomControl({
    //   name: '椭圆',
    //   block: 'draw',
    //   onClick: () => {
    //     // 椭圆的中心点
    //     const latlng = this._map.getCenter()
    //     // 椭圆的参数
    //     const majorRadius = 200 // 横轴半径，单位为米
    //     const minorRadius = 100 // 纵轴半径，单位为米
    //     const axis = 'north' // 椭圆的轴方向，可以是'center', 'west', 'east', 'north' 或 'south'
    //     const rotation = 40 // 椭圆的角度，单位为度
    //     // 绘制椭圆
    //     L.ellipse(latlng, [majorRadius, minorRadius], rotation, {
    //       axis,
    //       color: 'red',
    //       weight: 1,
    //       fillOpacity: 0.2,
    //       fillColor: '#f03'
    //     }).addTo(this._map)
    //   }
    // })

    //当一个图形被绘制完毕后调用，参数包含图形类型和工作图层。
    this._map.on('pm:create', (e: any) => {
      const drawnFeature = e.layer
      const featureType = e.shape
      switch (featureType) {
        case 'Rectangle':
        case 'Polygon':
        case 'Circle':
        case 'Line':
          e.layer.options = { ...this._options, shape: e.shape } as any
          break
      }
      // 根据绘制的图形类型处理区域信息
      switch (featureType) {
        case 'Circle':
          // const circleCenter = drawnFeature.getLatLng()
          // const circleRadius = drawnFeature.getRadius()
          // console.log('Drawn Circle:', {
          //   center: circleCenter,
          //   radius: circleRadius,
          // })
          break

        case 'Rectangle':
          // const rectangleBounds = drawnFeature.getBounds()
          // console.log('Drawn Rectangle:', {
          //   bounds: rectangleBounds.toBBoxString(),
          // })
          break

        case 'Polygon':
        case 'Polyline':
        case 'Line':
          // const vertices = drawnFeature.getLatLngs()
          // console.log(`Drawn ${featureType}:`, {
          //   vertices: vertices.map((latLng: any) => [latLng.lat, latLng.lng]),
          // })
          break

        default:
          console.log(`Unsupported feature type drawn: ${featureType}`)
      }
      geojsonLayers.addLayer(e.layer)
    })

    this._map.on('pm:drawstart', ({ workingLayer }) => {
      // 当一个节点被添加的时候调用，参数包含新的节点，它的marker，索引，工作图层和图形类型。
      workingLayer
        .on('pm:vertexadded', (e: any) => {
          console.log('点了一个点', e)
        })
        .on('pm:snapdrag', (e: any) => {
          console.log('在移动一个点中调用，参数包含影响图层和捕捉计算。', e)
        })
        .on('pm:snap', (e: any) => {
          console.log('当一个节点被捕捉后调用，参数同上。', e)
        })
        .on('pm:unsnap', (e: any) => {
          console.log('当一个节点失去捕捉后调用，参数同上。', e)
        })
        .on('pm:centerplaced', (e: any) => {
          console.log('当图形的中心点被替换或移动后调用。', e)
        })
    })
    //当绘制模式关闭的时候调用，参数包含图形类型。
    this._map
      .on('pm:drawend', (e: any) => {
        console.log(e)
      })
      .on('pm:update', (e) => {
        // 图形更新事件
        const updatedGeoJson = e.layer //.toGeoJSON()
        // 发送到服务器或更新本地存储
        console.log(updatedGeoJson)
      })
      .on('pm:remove', (e) => {
        // 图形删除事件
        const removedGeoJson = e.layer //.toGeoJSON()
        // 从服务器或本地存储中删除对应数据
        console.log(removedGeoJson)
      })
  }
  onClickGeojsonLayers(event: { layer: any }) {
    const clickedLayer = event.layer

    // 如果已有一个挂载的StyleEditor迷你应用，先卸载它
    if (clickedLayer._styleEditorApp) {
      clickedLayer._styleEditorApp.unmount()
    }

    let popup = clickedLayer.getPopup()
    if (!popup) {
      popup = L.popup()
      clickedLayer.bindPopup(popup)
    }

    // 创建并打开包含StyleEditor的Popup
    popup.setContent(
      `<div class="popup-content">
        <style-editor :layer="clickedLayer" :map="map"></style-editor>
      </div>`
    )
    clickedLayer.openPopup()

    // Vue3中使用createApp创建一个迷你应用来渲染StyleEditor组件
    const popupContentEl = document.querySelector('.popup-content')
    const popupApp = createApp(StyleEditor, {
      layer: clickedLayer,
      map: this._map,
    })
    popupContentEl && popupApp.mount(popupContentEl)
    clickedLayer._styleEditorApp = popupApp
  }
  remove(id?: string) {
    for (const iterator of Object.keys(this.gsonLayer)) {
      if (iterator === id || !id) {
        const { theCollection, marker } = this.gsonLayer[iterator]
        theCollection?.remove()
        if (marker) {
          this._map.removeLayer(marker)
          this.gsonLayer[iterator].marker = ''
        }
      }
    }
    if (!id) this.gsonLayer = {}
  }

  _DataEcho(id: string, geoJsonData?: GeoJsonObject | GeoJsonObject[]) {
    if (!geoJsonData) return
    const theCollection = L.geoJson(geoJsonData as any, {
      style: (feature: any) => {
        return {
          color: '#ff3333',
          weight: 2,
          opacity: 1,
          fillColor: '#ff3333',
          fillOpacity: 0.5,
          ...(feature.style || {}),
        }
      },
      pointToLayer: (feature: any, latlng) => {
        if (feature.properties.customGeometry) {
          return new L.Circle(latlng, feature.properties.customGeometry.radius)
        }
        if (feature.icon) {
          const m = new L.Marker(latlng, {
            icon: feature.icon,
          })
          if (feature.showHtml) {
            m.bindTooltip(`<em></em>${feature.showHtml}`, {
              direction: 'right',
              permanent: false,
              className: 'nav_tooltip',
              ...(feature.showHtmlOptions || {}),
            })
          }
          return m
        }
        return null as any
      },
      onEachFeature: (feature: any, layer) => {
        // 添加编辑
        // geojsonLayers.addLayer(layer)

        // 检查图层是否是多边形
        if (layer instanceof L.Polygon) {
          if (feature.nameShow && !this.gsonLayer[id]?.marker) {
            // 获取多边形的边界框
            const bounds = layer.getBounds()
            // 计算边界框的中心点
            const center = bounds.getCenter()
            const content = feature.name
            const styleToString = (style: { [x: string]: any }) => {
              return Object.keys(style).reduce(
                (acc, key) =>
                  `${
                    acc +
                    key
                      .split(/(?=[A-Z])/)
                      .join('-')
                      .toLowerCase()
                  }:${style[key]};`,
                ''
              )
            }
            const myIcon = L.divIcon({
              html: `<div style='${styleToString(
                feature.nameFont || {}
              )}'>${content}</div>`,
              className: 'my-div-icon',
              iconSize: new Point(0, 0),
            })

            //中心点位
            const marker = L.marker(center, { icon: myIcon }).addTo(this._map)

            this.gsonLayer[id] = {
              marker,
            }
          }
        }
        // 启用每个图层的编辑状态
        // ;(layer as any).pm.enable(true)
      },
    })
    theCollection.addTo(this._map)

    this.gsonLayer[id] = { theCollection, ...(this.gsonLayer[id] || {}) }
    // theCollection.pm.toggleEdit({
    //   draggable: true,
    //   snappable: true
    // })
    this._map.fitBounds(theCollection.getBounds())

    theCollection.on('pm:edit', (e) => {
      console.log('编辑结束', e)
    })
    theCollection.on('pm:dragstart', (e) => {
      console.log('拖拽开始', e)
    })
  }

  public drawPolygon(type: DrawType, options?: L.PM.DrawModeOptions) {
    const map = toRaw(this._map)
    map.pm.enableDraw(type, { ...this._options, ...options } as any)
  }
}
